import { useMemo } from 'react'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import { useStatsigExperiment } from '@src/experiments/statsig-integration'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { FILTER_TYPE } from '@src/server-side/feature-flags/feature-flag.types'

import { useCollectionFeature } from '../collection-feature-provider'
import { ECollectionPageType } from '../services/collectionService/CollectionService.types'

const DESKTOP_DRAWER_GROUPS = [STATSIG_EXPERIMENTS_BUCKETS.GROUP_A, STATSIG_EXPERIMENTS_BUCKETS.GROUP_C]
const MOBILE_DRAWER_GROUPS = [STATSIG_EXPERIMENTS_BUCKETS.GROUP_A, STATSIG_EXPERIMENTS_BUCKETS.GROUP_B]
const SIDEBAR_GROUPS = [STATSIG_EXPERIMENTS_BUCKETS.GROUP_B, STATSIG_EXPERIMENTS_BUCKETS.GROUP_D]
const ALL_DESKTOP_GROUPS = [...DESKTOP_DRAWER_GROUPS, ...SIDEBAR_GROUPS]

export interface UseStatsigNewCLPFilterExperimentReturn {
  isNewCLPFiltersExperimentActive: boolean
  newCLPFiltersDesktopBucket: STATSIG_EXPERIMENTS_BUCKETS | null
  newCLPFiltersMobileBucket: STATSIG_EXPERIMENTS_BUCKETS | null
  isNewCLPFiltersExperimentInitialized: boolean
  isNewCLPFiltersDesktopActive: boolean
  collectionPageType: ECollectionPageType
  showDesktopFilterWithSidebar: boolean
  showDesktopFilterWithDrawer: boolean
  showMobileFilterWithDrawer: boolean
}

export function useStatsigNewCLPFilterExperiment(): UseStatsigNewCLPFilterExperimentReturn {
  const { deviceType } = useDeviceInfo()
  const collectionFeature = useCollectionFeature()
  const isDesktop = useMemo(() => deviceType === DEVICE_TYPES.DESKTOP, [deviceType])

  const { experimentBucket: desktopBucket, init: isDesktopInitialized } = useStatsigExperiment(
    STATSIG_EXPERIMENTS.NEW_CLP_FILTERS_DWEB,
    isDesktop,
  )
  const { experimentBucket: mobileBucket, init: isMobileInitialized } = useStatsigExperiment(
    STATSIG_EXPERIMENTS.NEW_CLP_FILTERS_MWEB,
    !isDesktop,
  )

  return useMemo(() => {
    const isNewCLPFiltersDesktopActive =
      isDesktop &&
      (collectionFeature.filterType === FILTER_TYPE.DRAWER ||
        collectionFeature.filterType === FILTER_TYPE.SIDEBAR ||
        ALL_DESKTOP_GROUPS.includes(desktopBucket as STATSIG_EXPERIMENTS_BUCKETS))

    const showMobileFilterWithDrawer =
      !isDesktop &&
      (collectionFeature.filterType === FILTER_TYPE.BOTTOM_SHEET ||
        MOBILE_DRAWER_GROUPS.includes(mobileBucket as STATSIG_EXPERIMENTS_BUCKETS))

    const showDesktopFilterWithDrawer =
      isDesktop &&
      (collectionFeature.filterType === FILTER_TYPE.DRAWER ||
        DESKTOP_DRAWER_GROUPS.includes(desktopBucket as STATSIG_EXPERIMENTS_BUCKETS))

    const showDesktopFilterWithSidebar =
      isDesktop &&
      (collectionFeature.filterType === FILTER_TYPE.SIDEBAR ||
        SIDEBAR_GROUPS.includes(desktopBucket as STATSIG_EXPERIMENTS_BUCKETS))

    const isNewCLPFiltersExperimentActive = isNewCLPFiltersDesktopActive || showMobileFilterWithDrawer

    return {
      isNewCLPFiltersExperimentActive,
      newCLPFiltersDesktopBucket: desktopBucket as STATSIG_EXPERIMENTS_BUCKETS | null,
      newCLPFiltersMobileBucket: mobileBucket as STATSIG_EXPERIMENTS_BUCKETS | null,
      isNewCLPFiltersExperimentInitialized: (isDesktop && isDesktopInitialized) || (!isDesktop && isMobileInitialized),
      isNewCLPFiltersDesktopActive,
      collectionPageType: collectionFeature.collectionPageType,
      showDesktopFilterWithDrawer,
      showDesktopFilterWithSidebar,
      showMobileFilterWithDrawer,
    }
  }, [
    desktopBucket,
    mobileBucket,
    isDesktopInitialized,
    isMobileInitialized,
    isDesktop,
    collectionFeature.filterType,
    collectionFeature.collectionPageType,
  ])
}
